import { useState } from "react";
import { Button, Skeleton, EmptyState } from "@cntrl/ui";
import { Modal } from "../../releases/Modal.js";
import { useCapability } from "../../../app/bandContext.js";
import { useSignupQr } from "../../../app/hooks/useFans.js";
import { pushToast } from "../../../app/toasts.js";
import styles from "./FanForm.module.scss";

/**
 * The signup-QR modal — the printable merch-table QR that grows the fan list. The QR is generated
 * IN-HOUSE server-side (DROP-19, never an external QR service); it links to the public `/:band/signup` page whose
 * form POSTs an idempotent, rate-limited fan upsert (source='qr'). Read-only display: scan-to-preview, copy the URL,
 * or print. fans:read gated (deep-link guarded).
 */
export function SignupQrModal({ onClose }: { onClose: () => void }) {
  const canRead = useCapability("fans:read");
  const q = useSignupQr(canRead);
  const [copied, setCopied] = useState(false);

  if (!canRead) return null; // deep-link guard (the API also 403s the fan surface)

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      pushToast("Couldn't copy — select and copy the link manually.", "info");
    }
  }

  return (
    <Modal title="Signup QR" size="md" onClose={onClose}>
      {q.isLoading || !q.data ? (
        q.isError ? (
          <EmptyState title="Couldn't load the QR" description="Something went wrong." />
        ) : (
          <div role="status" aria-label="Loading"><Skeleton variant="line" width="70%" /></div>
        )
      ) : (
        <div className={styles.qrWrap}>
          {q.data.qrSvgDataUri && (
            <img className={styles.qrImg} src={q.data.qrSvgDataUri} alt="QR code linking to your public signup page" />
          )}
          <p className={styles.note}>Print it for the merch table — fans scan to join your list.</p>
          {q.data.signupUrl && (
            <div className={styles.qrUrl}>
              <span className={styles.qrUrlValue} title={q.data.signupUrl}>{q.data.signupUrl}</span>
              <Button type="button" variant="ghost" onClick={() => void copy(q.data!.signupUrl!)}>
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          )}
          <div className={styles.actionsEnd}>
            <Button type="button" variant="ghost" onClick={() => window.print()}>Print</Button>
            <Button type="button" variant="primary" onClick={onClose}>Done</Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
